import { useCallback, useState } from "react";

import { classification as classificationApi } from "../../api/resources";
import type { AssistantClassification, AssistantDestination } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { Card, EmptyState, ErrorBanner, Loading, StatusBadge } from "../ui";

/**
 * The reviewer's view of what the assistant thought an answer was.
 *
 * The wording here does a job. "Accept routing" is not "Accept" — accepting says where an
 * observation goes and nothing about whether the answer is true, and the panel says so on every
 * row so nobody has to remember it.
 */

export interface ClassificationReviewProps {
  organizationId: string;
  /** Show only what a person should actually look at: ambiguous reads and mismatches. */
  needsReviewOnly?: boolean;
}

function confidence(value: string | null): string {
  if (value === null) return "—";
  const number = Number(value);
  return Number.isNaN(number) ? value : `${Math.round(number * 100)}%`;
}

const SOURCE_LABEL: Record<string, string> = {
  question: "the question's declaration",
  model: "the assistant",
  heuristic: "the answer's shape",
  human: "a person",
};

export function ClassificationReview({
  organizationId,
  needsReviewOnly = false,
}: ClassificationReviewProps) {
  const [nonce, setNonce] = useState(0);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const [changing, setChanging] = useState<string>();
  const [asking, setAsking] = useState<string>();
  const [askText, setAskText] = useState("");

  const rows = useAsync(
    () =>
      classificationApi.list(organizationId, {
        needs_review: needsReviewOnly || undefined,
        limit: 50,
      }),
    [organizationId, needsReviewOnly, nonce],
  );
  const destinations = useAsync(
    () => classificationApi.destinations(organizationId),
    [organizationId],
  );

  const act = useCallback(
    async (id: string, run: () => Promise<unknown>) => {
      setBusy(id);
      setError(undefined);
      try {
        await run();
        setNonce((value) => value + 1);
        setChanging(undefined);
        setAsking(undefined);
        setAskText("");
      } catch {
        setError("That could not be recorded.");
      } finally {
        setBusy(undefined);
      }
    },
    [],
  );

  const options: AssistantDestination[] = destinations.data?.destinations ?? [];

  return (
    <Card title="What the assistant thought these answers were">
      <p className="small muted" style={{ margin: "0 0 8px" }}>
        Routing metadata, not meaning. Accepting one says where an observation goes — it does not
        confirm the answer, approve a candidate or publish anything.
      </p>
      {destinations.data ? (
        <p className="tiny faint" style={{ margin: "0 0 8px" }}>
          Precedence: {destinations.data.precedence.join(" → ")}
        </p>
      ) : null}

      <ErrorBanner error={error ?? rows.error} />
      {rows.loading ? <Loading /> : null}

      {rows.data && rows.data.length === 0 ? (
        <EmptyState
          title={needsReviewOnly ? "Nothing needs a person" : "No classifications yet"}
          description={
            needsReviewOnly
              ? "Every classification either agrees with the question or is clear enough to route."
              : "They appear as answers are classified."
          }
        />
      ) : null}

      {(rows.data ?? []).map((row: AssistantClassification) => (
        <div key={row.id} className="classification">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <div>
              <div className="mono small">{row.semantic_id}</div>
              <div className="truncate">{row.answer_excerpt}</div>
            </div>
            <StatusBadge status={row.status} />
          </div>

          <dl className="classification__facts">
            <dt>Routed as</dt>
            <dd>
              <strong>{row.destination.replace(/_/g, " ").toLowerCase()}</strong>
              {row.candidate_kind ? ` · ${row.candidate_kind}` : ""}
              {row.concept ? ` · ${row.concept}` : ""}
              {row.field_name ? `.${row.field_name}` : ""}
            </dd>
            <dt>Decided by</dt>
            <dd>{SOURCE_LABEL[row.source] ?? row.source}</dd>
            <dt>Confidence</dt>
            <dd>
              {confidence(row.confidence)}
              {row.ambiguity ? " · ambiguous" : ""}
            </dd>
            {row.rationale ? (
              <>
                <dt>Why</dt>
                <dd>{row.rationale}</dd>
              </>
            ) : null}
          </dl>

          {row.mismatch ? (
            <div className="banner banner--warn">
              <strong>The assistant read this differently from how the question is declared.</strong>{" "}
              {Object.entries(row.mismatch.differences)
                .map(
                  ([name, diff]) =>
                    `${name}: declared ${diff.declared}, suggested ${diff.suggested}`,
                )
                .join("; ")}
              . {row.mismatch.note}
            </div>
          ) : null}

          {row.status === "suggested" ? (
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn"
                disabled={busy === row.id}
                onClick={() => act(row.id, () => classificationApi.accept(organizationId, row.id))}
              >
                Accept routing
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setChanging(changing === row.id ? undefined : row.id)}
              >
                Change
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setAsking(asking === row.id ? undefined : row.id);
                  setAskText(row.clarifying_question ?? "");
                }}
              >
                Ask respondent to clarify
              </button>
            </div>
          ) : (
            <p className="tiny faint">
              {row.status.replace(/_/g, " ")} by {row.reviewed_by ?? "somebody"}
              {row.reviewed_at ? ` on ${new Date(row.reviewed_at).toLocaleDateString()}` : ""}
              {row.context_task_id ? " · a task was created" : ""}
            </p>
          )}

          {changing === row.id ? (
            <div className="row" style={{ gap: 6 }}>
              <select
                aria-label="New destination"
                defaultValue={row.destination}
                onChange={(event) =>
                  act(row.id, () =>
                    classificationApi.change(organizationId, row.id, {
                      destination: event.target.value,
                      candidate_kind: row.candidate_kind,
                      concept: row.concept,
                      field_name: row.field_name,
                    }),
                  )
                }
              >
                {options.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {asking === row.id ? (
            <div className="stack">
              <textarea
                rows={2}
                value={askText}
                aria-label="Clarifying question"
                placeholder="What would settle this?"
                onChange={(event) => setAskText(event.target.value)}
              />
              <div>
                <button
                  type="button"
                  className="btn"
                  disabled={!askText.trim() || busy === row.id}
                  onClick={() =>
                    act(row.id, () =>
                      classificationApi.clarify(organizationId, row.id, askText.trim()),
                    )
                  }
                >
                  Add to their tasks
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ))}
    </Card>
  );
}
